import React, { useState, useEffect } from "react";
import { Modal, Form, Select, message, Spin, Alert } from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { HierarchyUser } from "../../../utils/api/hierarchyAPI";
import { BASE_API_URL } from "../../../utils/consts";
import "./AssignManagerModal.scss";

const { Option } = Select;

interface AssignManagerModalProps {
  visible: boolean;
  user: HierarchyUser | null;
  clientId: string;
  onCancel: () => void;
  onSubmit: (managerId: string | null) => void;
}

const AssignManagerModal: React.FC<AssignManagerModalProps> = ({
  visible,
  user,
  clientId,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<HierarchyUser[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Load employees when modal opens
  useEffect(() => {
    if (visible && clientId) {
      loadEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, clientId]);

  // Set initial value when user changes
  useEffect(() => {
    if (user) {
      const currentManagerId =
        typeof user.managerId === "object"
          ? user.managerId?._id
          : user.managerId;
      form.setFieldsValue({
        managerId: currentManagerId || null,
      });
    }
  }, [user, form]);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_API_URL}/${clientId}/users`);

      // Filter out the current user (can't be their own manager)
      const filteredEmployees = response.data.filter(
        (emp: any) => emp._id !== user?._id
      );

      setEmployees(filteredEmployees);
    } catch (error) {
      console.error("Error loading employees:", error);
      message.error(t("Failed to load employees"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await onSubmit(values.managerId);
      form.resetFields();
    } catch (error) {
      console.error("Form validation error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Check if employee would create circular reference
  const wouldCreateCircularRef = (potentialManagerId: string): boolean => {
    if (!user) return false;

    // Check if potential manager is a subordinate of current user
    const checkSubordinates = (userId: string): boolean => {
      const employee = employees.find((emp) => emp._id === userId);
      if (!employee) return false;

      const managerId =
        typeof employee.managerId === "object"
          ? employee.managerId?._id
          : employee.managerId;

      if (managerId === user._id) return true;

      if (managerId) {
        return checkSubordinates(managerId);
      }

      return false;
    };

    return checkSubordinates(potentialManagerId);
  };

  const currentManagerId =
    typeof user?.managerId === "object"
      ? user?.managerId?._id
      : user?.managerId;

  return (
    <Modal
      title={
        <div className="modal-title">
          <UserOutlined />
          <span>{t("Assign Manager")}</span>
        </div>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={t("Assign")}
      cancelText={t("Cancel")}
      confirmLoading={submitting}
      width={600}
      className="assign-manager-modal"
    >
      {user && (
        <div className="user-info-section">
          <Alert
            message={
              <div>
                <strong>{t("Employee")}:</strong>{" "}
                {`${user.firstName} ${user.lastName}`}
              </div>
            }
            description={
              <div>
                <div>
                  <strong>{t("Position")}:</strong>{" "}
                  {user.position?.positionName || t("No Position")}
                </div>
                {currentManagerId && (
                  <div>
                    <strong>{t("Current Manager")}:</strong>{" "}
                    {typeof user.managerId === "object"
                      ? `${user.managerId.firstName} ${user.managerId.lastName}`
                      : t("Unknown")}
                  </div>
                )}
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="managerId"
          label={t("Select Manager")}
          rules={[
            {
              validator: async (_, value) => {
                if (value && wouldCreateCircularRef(value)) {
                  throw new Error(
                    t("This would create a circular reference in the hierarchy")
                  );
                }
              },
            },
          ]}
        >
          <Select
            placeholder={t("Select a manager or leave empty for no manager")}
            loading={loading}
            showSearch
            allowClear
            optionFilterProp="children"
            suffixIcon={loading ? <Spin size="small" /> : <SearchOutlined />}
            filterOption={(input, option) =>
              (option?.children?.toString() || "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {employees.map((emp) => {
              const isCircular = wouldCreateCircularRef(emp._id);
              const isCurrent = emp._id === currentManagerId;

              return (
                <Option
                  key={emp._id}
                  value={emp._id}
                  disabled={isCircular}
                  className={isCurrent ? "current-manager" : ""}
                >
                  <div className="employee-option">
                    <div className="employee-name">
                      {`${emp.firstName} ${emp.lastName}`}
                      {isCurrent && (
                        <span className="current-badge"> (Current)</span>
                      )}
                      {isCircular && (
                        <span className="circular-badge"> (Circular Ref)</span>
                      )}
                    </div>
                    <div className="employee-details">
                      {emp.position?.positionName || "No Position"} • Level{" "}
                      {emp.hierarchyLevel || 0}
                    </div>
                  </div>
                </Option>
              );
            })}
          </Select>
        </Form.Item>

        <Alert
          message={t("Note")}
          description={t(
            "Changing the manager will update the employee's hierarchy level and reporting structure. This action can be undone by reassigning the manager."
          )}
          type="warning"
          showIcon
        />
      </Form>
    </Modal>
  );
};

export default AssignManagerModal;
